import styled from "styled-components";
import { theme } from "../../../theme";
import TitleSmall from "../../atoms/typography/TitleSmall";
import CircleIcon from "../../atoms/CircleIcon";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import { useTranslation } from "react-i18next";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../../atoms/icons/ChevronIcons";
import DetailsButton from "../../atoms/buttons/DetailsButton";
import { Row } from "react-grid-system";
import { useState } from "react";
import dayjs from "dayjs";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import type {
  ExemptionableRuleModel,
  InvoiceAssessmentRuleModel,
  RuleOverrideModel,
} from "../../../models/InvoiceAssessment";
import { HorizontalSpacer } from "../../atoms/Spacers";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import Alert from "../Alert";
import CreateWhitelistModal from "./CreateWhitelistModal";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import Small from "../../atoms/typography/Small";
import type { UserModel } from "../../../models/UserModel";
import { FixIconSmall } from "../../atoms/icons/FixIcon";

interface Props {
  rules: InvoiceAssessmentRuleModel[];
  hideTitle?: boolean;
  clientIdentifier?: string;
  recipientIdentifier?: string;
  recipientName?: string;
  ruleOverrides?: RuleOverrideModel[];
}

const CheckItemContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  justify-content: space-between;

  border-bottom: 1px solid ${theme.colors.grey05};
`;

const AM_SYSTEM_EXTERNAL_LINK = "https://fs328.amsystem.com/";

const getUserFullName = (user?: UserModel) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ");

const T800Result: React.FC<Props> = ({
  rules,
  hideTitle,
  clientIdentifier,
  recipientIdentifier,
  recipientName,
  ruleOverrides,
}) => {
  const { t } = useTranslation();
  const [displayApprovedRules, setDisplayApprovedRules] = useState(false);

  const [selectedWhitelistRule, setSelectedWhitelistRule] = useState<
    ExemptionableRuleModel | undefined
  >(undefined);
  const [whitelistSuccessfullyCreated, setWhitelistSuccessfullyCreated] =
    useState(false);

  const failedResults = rules.filter((r) => !r.passed && !r.warning);
  const warningResults = rules.filter((r) => r.warning);
  const approvedResults = rules.filter((r) => r.passed && !r.warning);

  const isWhitelistable = (
    rule: InvoiceAssessmentRuleModel,
  ): rule is ExemptionableRuleModel =>
    !!rule.exemptionOption &&
    rule.exemptionOption.canCreateExemption &&
    !!clientIdentifier &&
    (rule.exemptionOption.relationType !== "RECIPIENT_REQUIRED" ||
      !!recipientIdentifier);

  const onWhitelistCreated = () => {
    setSelectedWhitelistRule(undefined);
    setWhitelistSuccessfullyCreated(true);
    setTimeout(() => setWhitelistSuccessfullyCreated(false), 3000);
  };

  const onClickOpenAmSystem = () => {
    window.open(AM_SYSTEM_EXTERNAL_LINK, "_blank");
  };

  if (rules.length === 0) {
    return (
      <div>
        {!hideTitle && (
          <TitleSmall color={theme.colors.primary}>T800</TitleSmall>
        )}
        <BodySmall color={theme.colors.textSecondary}>
          {t("invoiceAssessment.t800NoAssessment")}
        </BodySmall>
      </div>
    );
  }

  const getRuleOverride = (ruleId: string) =>
    ruleOverrides?.find((r) => r.invoiceAssessmentRuleId === ruleId);

  return (
    <div>
      {!hideTitle && <TitleSmall color={theme.colors.primary}>T800</TitleSmall>}
      <HorizontalSpacer spacing={0.5} />
      {failedResults.map((rule) => {
        const ruleOverride = getRuleOverride(rule.invoiceAssessmentRuleId);

        return (
          <CheckItemContainer key={rule.invoiceAssessmentRuleId}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <CircleIcon
                type={ruleOverride ? "custom" : "failed"}
                icon={
                  ruleOverride ? (
                    <FixIconSmall color={theme.colors.warning} />
                  ) : (
                    <CrossIcon color={theme.colors.error} />
                  )
                }
                color={
                  ruleOverride ? theme.colors.warning20 : theme.colors.error
                }
              />
              <div>
                <Body fontweight="500">{rule.ruleName}</Body>
                <BodySmall>{rule.message}</BodySmall>
                {ruleOverride && (
                  <Small fontweight="400" color={theme.colors.textSecondary}>
                    {t("invoiceAssessment.ruleManuallyApproved", {
                      date: dayjs(ruleOverride.createdAt).format("YYYY-MM-DD"),
                      name: getUserFullName(ruleOverride.overriddenByUser),
                      reason: ruleOverride.reason,
                    })}
                  </Small>
                )}
              </div>
            </div>
            {isWhitelistable(rule) && (
              <div>
                <OutlinedButton
                  title={t("invoiceAssessment.whitelistOption")}
                  color={theme.colors.primary}
                  onClick={() => setSelectedWhitelistRule(rule)}
                />
              </div>
            )}
            {rule.limitOption && (
              <div>
                <OutlinedButton
                  title={t("invoiceAssessment.searchExtendedLimit")}
                  color={theme.colors.primary}
                  onClick={onClickOpenAmSystem}
                />
              </div>
            )}
          </CheckItemContainer>
        );
      })}
      {warningResults.map((rule) => (
        <CheckItemContainer key={rule.invoiceAssessmentRuleId}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <CircleIcon type="warning" />
            <div>
              <Body fontweight="500">{rule.ruleName}</Body>
              <BodySmall>{rule.message}</BodySmall>
            </div>
          </div>
        </CheckItemContainer>
      ))}
      <SlideOutHeight isVisible={displayApprovedRules}>
        {approvedResults.map((rule) => (
          <CheckItemContainer key={rule.invoiceAssessmentRuleId}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <CircleIcon type="success" />
              <div>
                <Body fontweight="500">{rule.ruleName}</Body>
                <BodySmall>{rule.message}</BodySmall>
              </div>
            </div>
          </CheckItemContainer>
        ))}
      </SlideOutHeight>
      {approvedResults.length > 0 && (
        <Row nogutter justify="end">
          <DetailsButton
            color={theme.colors.primary}
            icon={
              displayApprovedRules ? (
                <ChevronSmallUpIcon />
              ) : (
                <ChevronSmallDownIcon />
              )
            }
            title={
              displayApprovedRules
                ? t("invoices.hideApproved")
                : t("invoices.displayApproved")
            }
            onClick={() => setDisplayApprovedRules(!displayApprovedRules)}
          />
        </Row>
      )}

      {selectedWhitelistRule && clientIdentifier && (
        <CreateWhitelistModal
          rule={selectedWhitelistRule}
          clientIdentifier={clientIdentifier}
          recipientIdentifier={recipientIdentifier}
          recipientName={recipientName}
          onClose={() => setSelectedWhitelistRule(undefined)}
          onCreated={onWhitelistCreated}
        />
      )}

      {whitelistSuccessfullyCreated && (
        <Alert
          type="success"
          title={t("invoiceAssessment.whitelistSuccessfullyCreated")}
          version="snackbar"
        />
      )}
    </div>
  );
};

export default T800Result;
